import gsap, { SplitText, Flip } from '@lib/gsap'
import { onMount, onView, onTrack, onDestroy, onPageIn, onPageOut } from "./_";
import { Raf } from "@lib/subs";
import { Scroll } from '@/lib/scroll';

export default function (element: HTMLElement, dataset: any) {
  // Track which elements already have hover handlers
  const initializedItems = new WeakSet<HTMLElement>();
  let mutationObserver: MutationObserver | null = null;
  const eventListeners = new Map<HTMLElement, { enter: () => void; leave: () => void; move: (e: MouseEvent) => void }>();
  const dotOriginalPositions = new Map<HTMLElement, Array<{ x: number; y: number }>>();
  const dotSetters = new Map<HTMLElement, Array<{ setX: (value: number) => void; setY: (value: number) => void; proxy: { x: number; y: number } }>>();
  const dotReturnAnimations = new Map<HTMLElement, Array<gsap.core.Tween>>();
  const radius = 4; // Distance from cursor to dots
  const rotationSpeed = 0.004; // Speed of rotation (radians per ms)
  const mousePositions = new Map<HTMLElement, { x: number; y: number; startTime: number }>();
  const hoveredItems = new Set<HTMLElement>();
  let rafUnsubscribe: (() => void) | null = null;

  // Function to apply hover handlers to a work item
  function setupHoverHandlers(workItem: HTMLElement) {
    // Skip if already initialized
    if (initializedItems.has(workItem)) {
      return;
    }

    // Get browser dots and store their original positions
    const dots = Array.from(workItem.querySelectorAll('.work_browser_dot')) as HTMLElement[];
    if (dots.length === 3) {
      const originalPositions = dots.map(dot => {
        const rect = dot.getBoundingClientRect();
        const itemRect = workItem.getBoundingClientRect();
        return {
          x: rect.left - itemRect.left + rect.width / 2,
          y: rect.top - itemRect.top + rect.height / 2
        };
      });
      dotOriginalPositions.set(workItem, originalPositions);

      // Create quickSetters for each dot
      const setters = dots.map(dot => {
        const setX = gsap.quickSetter(dot, "x", "px") as (value: number) => void;
        const setY = gsap.quickSetter(dot, "y", "px") as (value: number) => void;
        const proxy = { x: 0, y: 0 };
        return { setX, setY, proxy };
      });
      dotSetters.set(workItem, setters);
    }

    // Create event handlers
    const enterHandler = () => hoverIn(workItem);
    const leaveHandler = () => hoverOut(workItem);
    const moveHandler = (e: MouseEvent) => handleMouseMove(workItem, e);

    // Add event listeners
    workItem.addEventListener('mouseenter', enterHandler);
    workItem.addEventListener('mouseleave', leaveHandler);
    workItem.addEventListener('mousemove', moveHandler);

    // Track this element
    initializedItems.add(workItem);
    eventListeners.set(workItem, { enter: enterHandler, leave: leaveHandler, move: moveHandler });

    // Add 'loaded' class to trigger CSS transition
    workItem.classList.add('loaded');
  }

  // Function to initialize all existing work items
  function initializeExistingItems() {
    // Query within the module element, or fall back to document if not found
    const cardsToHover = element.querySelectorAll('.work_item');
    cardsToHover.forEach((e) => {
      setupHoverHandlers(e as HTMLElement);
    });
  }

  // Function to watch for new work items
  function watchForNewItems() {
    // Determine the container to observe
    // If work items are inside this element, observe the element itself
    // Otherwise, observe the parent or document.body for broader coverage
    const container = element.querySelector('.work_item')
      ? element
      : (element.parentElement || document.body);

    mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if the added node is a work item
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.classList.contains('work_item')) {
              Scroll.resize()
              element.style.transition = "opacity 0.8s ease"
              setupHoverHandlers(element);
            }
          }
        });
      });
    });

    // Start observing
    mutationObserver.observe(container, {
      childList: true,
      subtree: true
    });
  }

  onMount(() => {
    // Initialize existing items
    initializeExistingItems();

    // Start watching for new items
    watchForNewItems();

    gsap.set('.work_item', {
      scale: 0.92,
      yPercent: 10,
      opacity: 0,
    })
  });

  onDestroy(() => {
    // Disconnect the mutation observer
    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }

    // Remove all event listeners
    eventListeners.forEach((handlers, workItem) => {
      workItem.removeEventListener('mouseenter', handlers.enter);
      workItem.removeEventListener('mouseleave', handlers.leave);
      workItem.removeEventListener('mousemove', handlers.move);
    });
    eventListeners.clear();

    // Stop animation loop
    if (rafUnsubscribe) {
      rafUnsubscribe();
      rafUnsubscribe = null;
    }

    // Kill all dot return animations
    dotReturnAnimations.forEach((animations) => {
      animations.forEach(anim => anim.kill());
    });
    dotReturnAnimations.clear();
    dotOriginalPositions.clear();
    dotSetters.clear();
    mousePositions.clear();
    hoveredItems.clear();
  });

  onPageIn(async () => {
    await gsap.to('.work_item', {
      scale: 1,
      opacity: 1,
      stagger: 0.1,
      yPercent: 0,
      delay: 0.5
    })
  })

  // Animation options
  const opts = {
    duration: 0.5,
    ease: 'power1.inOut' as const
  }

  // Hover in handler
  function hoverIn(element: HTMLElement) {
    let image = element.querySelector('.work_image') as HTMLElement
    let container = element.querySelector('.work_image_wrap') as HTMLElement
    let state = Flip.getState(image)
    container.appendChild(image),
      Flip.from(state, {
        absolute: true,
        ...opts
      })

    // Mark as hovered and start animation loop if not already running
    hoveredItems.add(element);

    // Initialize mouse position if not already set (will be updated on first mousemove)
    if (!mousePositions.has(element)) {
      const rect = element.getBoundingClientRect();
      const startTime = performance.now();
      mousePositions.set(element, {
        x: rect.width / 2, // Center of element as default
        y: rect.height / 2,
        startTime
      });
    }

    if (!rafUnsubscribe) {
      startAnimationLoop();
    }
  }

  // Hover out handler
  function hoverOut(element: HTMLElement) {
    let image = element.querySelector('.work_image') as HTMLElement
    let container = element.querySelector('.work_image_item') as HTMLElement
    let state = Flip.getState(image)
    container.appendChild(image),
      Flip.from(state, {
        absolute: true,
        ...opts
      })

    // Remove from hovered items
    hoveredItems.delete(element);
    mousePositions.delete(element);

    // Stop animation loop if no items are hovered
    if (hoveredItems.size === 0 && rafUnsubscribe) {
      rafUnsubscribe();
      rafUnsubscribe = null;
    }

    // Animate dots back to original positions
    animateDotsToOriginal(element);
  }

  // Handle mouse move - just update stored mouse position
  function handleMouseMove(workItem: HTMLElement, e: MouseEvent) {
    const rect = workItem.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Update mouse position (preserve startTime from hover in)
    let mousePos = mousePositions.get(workItem);
    if (!mousePos) {
      // Fallback if somehow not initialized (shouldn't happen)
      const startTime = performance.now();
      mousePos = { x: mouseX, y: mouseY, startTime };
      mousePositions.set(workItem, mousePos);
    } else {
      mousePos.x = mouseX;
      mousePos.y = mouseY;
    }

    // Kill any return animations when mouse moves
    const returnAnims = dotReturnAnimations.get(workItem);
    if (returnAnims) {
      returnAnims.forEach(anim => anim.kill());
      dotReturnAnimations.delete(workItem);
    }
  }

  // Start continuous animation loop
  function startAnimationLoop() {
    rafUnsubscribe = Raf.add(() => {
      const currentTime = performance.now();
      // Update all hovered items
      hoveredItems.forEach(workItem => {
        updateDotsPosition(workItem, currentTime);
      });
    });
  }

  // Update dot positions based on stored mouse position and time
  function updateDotsPosition(workItem: HTMLElement, currentTime: number) {
    const dots = Array.from(workItem.querySelectorAll('.work_browser_dot')) as HTMLElement[];
    const setters = dotSetters.get(workItem);
    if (dots.length !== 3 || !setters) return;

    const mousePos = mousePositions.get(workItem);
    if (!mousePos) return;

    const originalPositions = dotOriginalPositions.get(workItem);
    if (!originalPositions) return;

    // Base angles for 3 dots (120 degrees apart)
    const baseAngles = [0, 120, 240].map(deg => (deg * Math.PI) / 180);

    dots.forEach((dot, index) => {
      const { setX, setY, proxy } = setters[index];
      const originalPos = originalPositions[index];

      // Get base position (original position relative to work item)
      const baseX = originalPos.x;
      const baseY = originalPos.y;

      // Calculate angle with rotation offset (sequential rotation)
      // Use elapsed time since hover started for consistent rotation
      const elapsedTime = currentTime - mousePos.startTime;
      const rotationOffset = (elapsedTime * rotationSpeed) + (index * (Math.PI * 2 / 3));
      const angle = baseAngles[index] + rotationOffset;

      // Calculate target position relative to mouse
      const targetX = mousePos.x + Math.cos(angle) * radius;
      const targetY = mousePos.y + Math.sin(angle) * radius;

      // Calculate transform values (relative to base position)
      const targetXTransform = targetX - baseX;
      const targetYTransform = targetY - baseY;

      // Use quickSetter with smooth easing via gsap.to on proxy
      gsap.to(proxy, {
        x: targetXTransform,
        y: targetYTransform,
        duration: 2,
        ease: 'power3.out',
        delay: index * 0.001,
        overwrite: true,
        onUpdate: () => {
          setX(proxy.x);
          setY(proxy.y);
        }
      });
    });
  }

  // Animate dots back to their original positions
  function animateDotsToOriginal(workItem: HTMLElement) {
    const dots = Array.from(workItem.querySelectorAll('.work_browser_dot')) as HTMLElement[];
    const originalPositions = dotOriginalPositions.get(workItem);
    const setters = dotSetters.get(workItem);

    if (!originalPositions || !setters || dots.length !== 3) return;

    // Clear mouse position tracking
    mousePositions.delete(workItem);

    const newAnims: gsap.core.Tween[] = [];

    dots.forEach((dot, index) => {
      const originalPos = originalPositions[index];
      const { setX, setY, proxy } = setters[index];

      // Get current transform values from proxy
      const currentX = proxy.x;
      const currentY = proxy.y;

      // Target is 0,0 (back to original position)
      const targetX = 0;
      const targetY = 0;

      // Animate back to original position using quickSetter
      const anim = gsap.to(proxy, {
        x: targetX,
        y: targetY,
        duration: 0.9,
        ease: 'power3.inOut',
        overwrite: true,
        delay: index * 0.05, // Sequential return animation
        onUpdate: () => {
          setX(proxy.x);
          setY(proxy.y);
        }
      });

      newAnims.push(anim);
    });

    dotReturnAnimations.set(workItem, newAnims);
  }
}